import { Link } from "react-router";
import { Card } from "../../primitives/card";
import { Button } from "../../primitives/button";
import { Building2, ExternalLink } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { paths } from "@shared-core/routing/routes";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { formatIssueAge, isIssueResolved } from "@shared-core/domain/issues";
import type { IssueProgressData } from "@shared-core/domain/issues";
import { IssueProgressTrail } from "./IssueProgressTrail";

/** Minimal issue data needed by the working header. */
interface WorkingHeaderIssue {
  status: string;
  source: string;
  assignee_id: string | null;
  assignee_name: string | null;
  customer_id: string | null;
  customer_name: string | null;
  raised_at: string;
  raised_by_id: string;
  identified_by_id: string | null;
  identified_by_name: string | null;
  age_days: number;
  related_reference: string | null;
  initial_investigation_department: string | null;
  resolved_at: string | null;
  resolved_by_id: string | null;
  legacy_crm_url?: string | null;
}

interface IssueWorkingHeaderProps {
  issue: WorkingHeaderIssue;
  canInvestigate: boolean;
  currentUserId: string;
  onStartInvestigation: () => void;
  onAssign: (userId: string | null) => void;
  onReopen: () => void;
}

function MetaCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="text-sm font-medium truncate">{children}</div>
    </div>
  );
}

export function IssueWorkingHeader({
  issue,
  canInvestigate,
  currentUserId,
  onStartInvestigation,
  onAssign,
  onReopen,
}: IssueWorkingHeaderProps) {
  const capabilities = usePlatformCapabilities();
  const isLegacy = issue.source === "legacy";
  const editable = canInvestigate && !isLegacy;
  const resolved = isIssueResolved(issue);

  const progressData: IssueProgressData = {
    status: issue.status,
    raised_at: issue.raised_at,
    raised_by_id: issue.raised_by_id,
    assignee_id: issue.assignee_id,
    resolved_at: issue.resolved_at,
    resolved_by_id: issue.resolved_by_id,
  };

  return (
    <Card className="p-5">
      <div className="grid gap-5 lg:grid-cols-[1fr_auto]">
        <div className="space-y-4 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <MetaCell label="Status">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="capitalize">{issue.status}</span>
                {editable && issue.status === "open" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onStartInvestigation}
                  >
                    Start investigation
                  </Button>
                )}
                {editable && resolved && (
                  <Button size="sm" variant="outline" onClick={onReopen}>
                    Reopen
                  </Button>
                )}
              </div>
            </MetaCell>

            <MetaCell label={isLegacy ? "Logged by" : "Investigator"}>
              {editable && !resolved ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span>
                    {issue.assignee_name ?? issue.assignee_id ?? "Unassigned"}
                  </span>
                  {issue.assignee_id === null ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAssign(currentUserId)}
                    >
                      Assign to me
                    </Button>
                  ) : issue.assignee_id === currentUserId ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onAssign(null)}
                    >
                      Unassign
                    </Button>
                  ) : null}
                </div>
              ) : (
                <span>
                  {issue.assignee_name ?? issue.assignee_id ?? "Unassigned"}
                </span>
              )}
            </MetaCell>

            <MetaCell label="Customer">
              {issue.customer_id && issue.customer_name ? (
                <Link
                  to={paths.customerDetail({ customerId: issue.customer_id })}
                  className="flex items-center gap-1 text-[color:var(--color-primary)] hover:underline"
                >
                  <Building2 className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{issue.customer_name}</span>
                </Link>
              ) : (
                <span className="text-muted-foreground">
                  Internal — no customer
                </span>
              )}
            </MetaCell>

            <MetaCell label="Raised">
              {formatDateOrDash(issue.raised_at)}
            </MetaCell>

            <MetaCell label="Age">
              {resolved ? "Resolved" : formatIssueAge(issue.age_days)}
            </MetaCell>

            {issue.related_reference && (
              <MetaCell label="Related">{issue.related_reference}</MetaCell>
            )}

            {issue.identified_by_id && (
              <MetaCell label="Identified by">
                {issue.identified_by_name ?? issue.identified_by_id}
              </MetaCell>
            )}

            {issue.initial_investigation_department && (
              <MetaCell label="Initial investigation by">
                {issue.initial_investigation_department}
              </MetaCell>
            )}
          </div>

          {isLegacy && issue.legacy_crm_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                capabilities.openExternalUrl(issue.legacy_crm_url!)
              }
            >
              <ExternalLink className="w-4 h-4" />
              View in legacy CRM
            </Button>
          )}
          {!canInvestigate && (
            <p className="text-sm text-muted-foreground">
              {resolved
                ? "This issue has been resolved. Investigation detail is restricted to managers."
                : "A manager is handling the investigation. You can see its progress below."}
            </p>
          )}
        </div>

        <div className="lg:border-l lg:border-border lg:pl-5">
          <div className="text-xs text-muted-foreground mb-3">Progress</div>
          <IssueProgressTrail issue={progressData} />
        </div>
      </div>
    </Card>
  );
}
